/**
 * services/AICredentialService.ts
 * Secure client-side credential storage for AI provider API keys
 *
 * Manages CRUD operations for AI provider credentials stored in IndexedDB
 * with AES-GCM encryption at rest. API keys never leave the browser and
 * are never synced to the server.
 *
 * Encryption flow:
 * 1. Device encryption key lazily generated on first save (AES-GCM 256-bit)
 * 2. Key material exported and stored in `ai_encryption_keys` table
 * 3. API keys encrypted with device key before storage in `ai_credentials`
 * 4. Decrypted keys held in memory only during API calls
 *
 * Fail-closed: if `crypto.subtle` is unavailable, credential save
 * operations throw rather than storing keys in plaintext.
 *
 * @see src/types/shared/AI.ts for AICredential and AIEncryptionKeyRecord types
 * @see src/utils/aiCrypto.ts for encryption utilities
 */

import { db } from '@/lib/db';
import type {
  AICredential,
  AIEncryptionKeyRecord,
  AIProviderType,
  AIValidationStatus,
} from '@/types/shared/AI';
import {
  isCryptoSubtleAvailable,
  generateAESKey,
  exportKeyToBase64,
  importKeyFromBase64,
  encryptString,
  decryptString,
} from '@/utils/aiCrypto';
import { generateUUID } from '@/utils/uuid';

// ─── Constants ──────────────────────────────────────────────────────────────

/** Fixed ID for the single device encryption key record */
const DEVICE_KEY_ID = 'device-key';

// ─── Service Class ──────────────────────────────────────────────────────────

/**
 * AI Credential Storage Service
 *
 * Singleton service managing encrypted API key storage in IndexedDB.
 * One credential per provider (upsert pattern).
 *
 * Features:
 * - AES-GCM encryption at rest via Web Crypto API
 * - Lazy device key generation (created on first credential save)
 * - In-memory CryptoKey cache (avoids repeated imports from IndexedDB)
 * - Fail-closed: throws if crypto.subtle unavailable (no plaintext fallback)
 * - Key loss detection (clears orphaned credentials if device key missing)
 */
class AICredentialService {
  // ===== PRIVATE STATE =====

  /** Cached CryptoKey for the session (avoids repeated IndexedDB lookups) */
  private cachedCryptoKey: CryptoKey | null = null;

  /** Whether encryption is available (cached after first check) */
  private encryptionAvailable: boolean | null = null;

  // ===== PRIVATE HELPERS: ENCRYPTION KEY =====

  /**
   * Check if encryption is available on this device
   *
   * Caches the result after first check. Public so the UI can check
   * proactively before prompting the user to enter an API key.
   */
  isEncryptionAvailable(): boolean {
    if (this.encryptionAvailable === null) {
      this.encryptionAvailable = isCryptoSubtleAvailable();
    }
    return this.encryptionAvailable;
  }

  /**
   * Get or create the device encryption key
   *
   * Lazily generates the AES-GCM key on first call, exports it to IndexedDB,
   * and caches the CryptoKey in memory for the session.
   *
   * Returns null if encryption is unavailable (graceful degradation).
   */
  private async getOrCreateDeviceKey(): Promise<CryptoKey | null> {
    // Return cached key if available
    if (this.cachedCryptoKey) {
      return this.cachedCryptoKey;
    }

    // Check encryption availability
    if (!this.isEncryptionAvailable()) {
      return null;
    }

    // Try to load existing key from IndexedDB
    const existingRecord = await db.ai_encryption_keys.get(DEVICE_KEY_ID);

    if (existingRecord) {
      try {
        const key = await importKeyFromBase64(existingRecord.exportedKey);
        this.cachedCryptoKey = key;
        return key;
      } catch {
        // Key import failed — corrupted record. Delete and regenerate.
        await db.ai_encryption_keys.delete(DEVICE_KEY_ID);
      }
    }

    // Generate new key
    const newKey = await generateAESKey();
    if (!newKey) {
      return null;
    }

    // Export and store key material
    const exportedKey = await exportKeyToBase64(newKey);
    const record: AIEncryptionKeyRecord = {
      id: DEVICE_KEY_ID,
      exportedKey,
      createdAt: new Date().toISOString(),
    };
    await db.ai_encryption_keys.put(record);

    // Cache for session
    this.cachedCryptoKey = newKey;
    return newKey;
  }

  // ===== PRIVATE HELPERS: ENCRYPT / DECRYPT =====

  /**
   * Encrypt an API key for storage
   *
   * @throws Error if encryption is unavailable (fail-closed — no plaintext fallback)
   */
  private async encryptApiKey(
    apiKey: string
  ): Promise<{ encryptedKey: string; iv: string }> {
    const deviceKey = await this.getOrCreateDeviceKey();

    if (!deviceKey) {
      throw new Error(
        'Encryption unavailable — cannot securely store API key'
      );
    }

    const { ciphertext, iv } = await encryptString(apiKey, deviceKey);
    return { encryptedKey: ciphertext, iv };
  }

  /**
   * Decrypt a stored API key
   *
   * @throws Error if the credential has no IV (corrupted record) or if
   *         the encryption key is unavailable on this device
   */
  private async decryptApiKey(credential: AICredential): Promise<string> {
    if (!credential.iv) {
      throw new Error(
        'Credential record is corrupted — missing IV. Please re-enter your API key.'
      );
    }

    const deviceKey = await this.getOrCreateDeviceKey();

    if (!deviceKey) {
      throw new Error(
        'Cannot decrypt credential: encryption key unavailable on this device'
      );
    }

    return decryptString(credential.encryptedKey, credential.iv, deviceKey);
  }

  // ===== PUBLIC API =====

  /**
   * Save or update an API key credential for a provider
   *
   * Upsert pattern: if a credential already exists for the provider, it is
   * replaced. The API key is encrypted before storage.
   *
   * @param provider - AI provider to save the key for
   * @param apiKey - Plaintext API key to encrypt and store
   * @param validationStatus - Initial validation status (default: 'untested')
   * @returns The saved AICredential record
   */
  async saveCredential(
    provider: AIProviderType,
    apiKey: string,
    validationStatus: AIValidationStatus = 'untested'
  ): Promise<AICredential> {
    const { encryptedKey, iv } = await this.encryptApiKey(apiKey);
    const now = new Date().toISOString();

    // Check for existing credential for this provider
    const existing = await db.ai_credentials
      .where('provider')
      .equals(provider)
      .first();

    // Resolve validatedAt — only include if we have a concrete string value
    // (exactOptionalPropertyTypes forbids explicit undefined assignment)
    const resolvedValidatedAt =
      validationStatus === 'valid' ? now : existing?.validatedAt;

    const credential: AICredential = {
      id: existing?.id ?? generateUUID(),
      provider,
      encryptedKey,
      iv,
      validationStatus,
      ...(resolvedValidatedAt != null && { validatedAt: resolvedValidatedAt }),
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    };

    await db.ai_credentials.put(credential);
    return credential;
  }

  /**
   * Get the credential record for a provider (without decryption)
   *
   * Returns the encrypted credential metadata. Use `getDecryptedKey()`
   * to retrieve the plaintext API key.
   *
   * @param provider - AI provider to look up
   * @returns AICredential record, or undefined if no key is stored
   */
  async getCredential(
    provider: AIProviderType
  ): Promise<AICredential | undefined> {
    return db.ai_credentials.where('provider').equals(provider).first();
  }

  /**
   * Get the decrypted API key for a provider
   *
   * Decrypts the stored credential and returns the plaintext key.
   * The key should be used immediately and not stored in persistent state.
   *
   * @param provider - AI provider to get the key for
   * @returns Decrypted API key string, or undefined if no key is stored
   */
  async getDecryptedKey(
    provider: AIProviderType
  ): Promise<string | undefined> {
    const credential = await this.getCredential(provider);
    if (!credential) {
      return undefined;
    }

    try {
      return await this.decryptApiKey(credential);
    } catch {
      // Decryption failed — likely key loss (user cleared IndexedDB)
      // Remove the orphaned credential to avoid confusion
      await db.ai_credentials.delete(credential.id);
      return undefined;
    }
  }

  /**
   * Delete the credential for a provider
   *
   * Hard-delete (not tombstone) since credentials are device-local
   * and never synced.
   *
   * @param provider - AI provider to delete the key for
   * @returns true if a credential was deleted, false if none existed
   */
  async deleteCredential(provider: AIProviderType): Promise<boolean> {
    const credential = await this.getCredential(provider);
    if (!credential) {
      return false;
    }

    await db.ai_credentials.delete(credential.id);
    return true;
  }

  /**
   * List all providers that have stored credentials
   *
   * Returns provider types only (no key data). Useful for UI display
   * of which providers are configured.
   *
   * @returns Array of AIProviderType values with stored credentials
   */
  async listProviders(): Promise<AIProviderType[]> {
    const credentials = await db.ai_credentials.toArray();
    return credentials.map((c) => c.provider);
  }

  /**
   * Update the validation status of a stored credential
   *
   * Called after key validation completes. Updates the status and
   * optionally the validatedAt timestamp.
   *
   * @param provider - AI provider to update
   * @param status - New validation status
   * @returns true if the credential was updated, false if none existed
   */
  async updateValidationStatus(
    provider: AIProviderType,
    status: AIValidationStatus
  ): Promise<boolean> {
    const credential = await this.getCredential(provider);
    if (!credential) {
      return false;
    }

    const now = new Date().toISOString();
    const resolvedValidatedAt =
      status === 'valid' ? now : credential.validatedAt;

    await db.ai_credentials.update(credential.id, {
      validationStatus: status,
      ...(resolvedValidatedAt != null && { validatedAt: resolvedValidatedAt }),
      updatedAt: now,
    });

    return true;
  }

  /**
   * Delete all credentials and the device encryption key
   *
   * Full reset — removes all stored API keys and the encryption key.
   * Used for account cleanup or troubleshooting.
   */
  async clearAll(): Promise<void> {
    await db.ai_credentials.clear();
    await db.ai_encryption_keys.clear();
    this.cachedCryptoKey = null;
  }
}

// ─── Singleton Export ───────────────────────────────────────────────────────

export { AICredentialService };
export const aiCredentialService = new AICredentialService();
